import { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { listSoilSightScenarios } from "services";

import {
  getUserFacingApiErrorMessage,
  setStoredSoilSightProjectId,
  useCreateSoilSightProject,
  useReplaceSoilSightCropYears,
  useTranslation,
  useUpdateSoilSightProjectSettingsRefinements,
  useUserContext,
} from "utils";

import { LIST_SOIL_SIGHT_SCENARIOS } from "consts";

import { Message } from "components";

import { useSoilSightLocationRegion } from "pages/app/scenario-analysis/hooks/useSoilSightLocationRegion";
import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { type PersistSoilSightFieldImportResult, persistSoilSightFieldImport } from "pages/app/scenario-analysis/utils/persistSoilSightFieldImport";

type PersistFieldImportArgs = {
  klimFarmId: string;
  fieldId: string;
  cropYears: CropYear[];
  /** The project the import goes into; null creates a new one. */
  getTargetProjectId: () => string | null;
  /** Stores the project id the import went into; defaults to the active-project slot. */
  storeProjectId?: (projectId: string) => void;
  /** Name for a newly created project; defaults to "Project 1". */
  name?: string;
  knownReferenceScenarioId?: string | null;
};

/** The one place a field import is persisted: mutations, region, names, the post-import refresh and the error toast. */
export const useFieldImportPersist = () => {
  const { t } = useTranslation("scenarioAnalysis");
  const queryClient = useQueryClient();
  const { reloadUserData } = useUserContext();
  const locationRegion = useSoilSightLocationRegion();

  const createProject = useCreateSoilSightProject();
  const replaceCropYears = useReplaceSoilSightCropYears();
  const updateRefinements = useUpdateSoilSightProjectSettingsRefinements();
  const { mutateAsync: createProjectAsync } = createProject;
  const { mutateAsync: replaceCropYearsAsync } = replaceCropYears;
  const { mutateAsync: updateRefinementsAsync } = updateRefinements;

  const isPersisting = createProject.isPending || replaceCropYears.isPending || updateRefinements.isPending;

  const persistFieldImport = useCallback(
    ({
      klimFarmId,
      fieldId,
      cropYears,
      getTargetProjectId,
      storeProjectId = setStoredSoilSightProjectId,
      name,
      knownReferenceScenarioId,
    }: PersistFieldImportArgs): Promise<PersistSoilSightFieldImportResult> =>
      persistSoilSightFieldImport({
        klimFarmId,
        fieldId,
        cropYears,
        locationRegion,
        referenceName: t("scenarioBuilder.reference"),
        name: name ?? t("projectManagement.defaultName", { n: 1 }),
        knownReferenceScenarioId,
        getStoredSoilSightProjectId: getTargetProjectId,
        setStoredSoilSightProjectId: storeProjectId,
        createProject: createProjectAsync,
        listSoilSightScenarios,
        replaceCropYears: replaceCropYearsAsync,
        updateRefinements: (args) => updateRefinementsAsync({ ...args, klimFarmId }),
      }),
    [createProjectAsync, locationRegion, replaceCropYearsAsync, t, updateRefinementsAsync]
  );

  // Refreshes what the /scenarios gate reads: user.farm.soilSightProjectId and, optionally, the project's scenario list.
  const refreshAfterImport = useCallback(
    async ({ soilSightProjectId, invalidateScenarios }: { soilSightProjectId: string; invalidateScenarios: boolean }) => {
      await Promise.all([
        invalidateScenarios ? queryClient.invalidateQueries({ queryKey: [LIST_SOIL_SIGHT_SCENARIOS, soilSightProjectId] }) : undefined,
        reloadUserData().catch(() => {}),
      ]);
    },
    [queryClient, reloadUserData]
  );

  const showImportError = useCallback(
    (error: unknown) => Message({ content: getUserFacingApiErrorMessage(error, t("onboarding.step2.submitError")), icon: "error" }),
    [t]
  );

  return { persistFieldImport, refreshAfterImport, showImportError, isPersisting, locationRegion };
};
